import { useMemo, useRef, useState, useCallback, useEffect } from 'react';

import { useFirebase } from '../../../hooks/useFirebase';

import { queryGet } from '../../tools/tools';
import { useQueryClient } from '@tanstack/react-query';

export const useAppUser = (user) => {
  const { remoteConfig } = useFirebase();
  const queryClient = useQueryClient();

  const [fetchedUser, setFetchedUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Tracks which uid the fetch below has actually resolved for. Firebase auth resolving to a
  // real user and this hook's own fetch starting are two separate state updates — on a hard
  // page load there's a render in between where `user` is already set but this effect hasn't
  // run yet, so `isLoading` (state) is still its stale value. Deriving loading from this ref
  // instead catches that gap synchronously during render, rather than depending on effect timing.
  const fetchedForUidRef = useRef(null);

  const fetchUser = useCallback(async () => {
    if (!user?.uid) {
      fetchedForUidRef.current = null;
      setFetchedUser(null);
      setIsLoading(false);
      return;
    }

    fetchedForUidRef.current = user.uid;
    setIsLoading(true);
    setError(null);

    try {
      const result = await queryGet(remoteConfig, `/users/uid/${user.uid}`);
      setFetchedUser(result);
      queryClient.setQueryData(['appUser', user.uid], result);
    } catch (err) {
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [remoteConfig, queryClient, user]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const isAdmin = useMemo(() => (fetchedUser ? fetchedUser.adminPanel : false), [fetchedUser]);
  const loading = isLoading || (!!user?.uid && fetchedForUidRef.current !== user.uid);

  return {
    appUser: fetchedUser,
    isAdmin,
    loading,
    error,
    refetch: fetchUser
  };
};
